import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Github, Search } from "lucide-react";
import { portfolioAPI } from "../../services/api";
import { ProjectsSkeleton } from "../../components/Skeleton/PageSkeletons";

const ProjectsContainer = styled.div`
  padding: 80px 24px 24px;
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 400;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 8px;
`;

const PageDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.5;
`;

const SearchContainer = styled.div`
  margin-bottom: 24px;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 8px 12px 8px 32px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.small};
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textPrimary};
  font-size: 14px;
  transition: all 0.15s ease;

  &::placeholder {
    color: ${props => props.theme.colors.textTertiary};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.focus};
    box-shadow: 0 0 0 2px rgba(0, 115, 187, 0.2);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: ${props => props.theme.colors.textTertiary};
  pointer-events: none;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled(motion.div)`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: ${props => props.theme.shadows.dropdown};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ProjectHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  h3 {
    font-size: 16px;
    font-weight: 500;
    color: ${props => props.theme.colors.textPrimary};
    margin: 0 0 4px 0;
    line-height: 1.4;
  }
`;

const ProjectContent = styled.div`
  padding: 20px;
`;

const ProjectDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 16px;
`;

const KeywordsContainer = styled.div`
  margin-bottom: 16px;
`;

const KeywordsLabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const KeywordsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const KeywordBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  background-color: ${props => props.theme.colors.surfaceSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: 11px;
  color: ${props => props.theme.colors.textSecondary};
  font-weight: 500;
`;

const ProjectActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ActionButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: ${props => props.theme.borderRadius.small};
  border: 1px solid ${props => props.theme.colors.border};
  background-color: transparent;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 12px;
  text-decoration: none;
  transition: all 0.15s ease;
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.colors.hover};
    color: ${props => props.theme.colors.textPrimary};
    border-color: ${props => props.theme.colors.primary};
  }

  &.primary {
    background-color: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    color: white;

    &:hover {
      background-color: ${props => props.theme.colors.primaryHover};
      border-color: ${props => props.theme.colors.primaryHover};
      color: white;
    }
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${props => props.theme.colors.textTertiary};

  h3 {
    font-size: 16px;
    font-weight: 400;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
  }
`;

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await portfolioAPI.getProjects();
        setProjects(response.data.data);
        setFilteredProjects(response.data.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = projects.filter(
        project =>
          project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          project.description
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          project.keywords.some(keyword =>
            keyword.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
      setFilteredProjects(filtered);
    } else {
      setFilteredProjects(projects);
    }
  }, [searchTerm, projects]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  if (loading) {
    return <ProjectsSkeleton />;
  }

  return (
    <ProjectsContainer>
      <Content>
        <PageHeader>
          <PageTitle>Projects</PageTitle>
          <PageDescription>
            A showcase of my software engineering projects and technical
            solutions
          </PageDescription>
        </PageHeader>

        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredProjects.length > 0 ? (
            <ProjectsGrid>
              {filteredProjects.map(project => (
                <ProjectCard
                  key={project.id}
                  variants={cardVariants}
                  whileHover={{ y: -2 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <ProjectHeader>
                    <h3>{project.title}</h3>
                  </ProjectHeader>

                  <ProjectContent>
                    <ProjectDescription>
                      {project.description}
                    </ProjectDescription>

                    <KeywordsContainer>
                      <KeywordsLabel>Technologies</KeywordsLabel>
                      <KeywordsList>
                        {project.keywords.map((keyword, index) => (
                          <KeywordBadge key={index}>{keyword}</KeywordBadge>
                        ))}
                      </KeywordsList>
                    </KeywordsContainer>

                    <ProjectActions>
                      {project.github && (
                        <ActionButton
                          href={project.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="primary"
                        >
                          <Github />
                          View Code
                        </ActionButton>
                      )}
                    </ProjectActions>
                  </ProjectContent>
                </ProjectCard>
              ))}
            </ProjectsGrid>
          ) : (
            <EmptyState>
              <h3>No projects found</h3>
              <p>Try adjusting your search criteria</p>
            </EmptyState>
          )}
        </motion.div>
      </Content>
    </ProjectsContainer>
  );
};

export default Projects;
